import { css } from 'aphrodite';
import * as React from 'react';
import { IAIAgentTemplate } from '../../../../extViewmodels/sdk/models/AI';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../web/components-v2/Alert';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogFooter,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { CardFeedback } from './CardFeedback';
import { CardMarkdown } from './CardMarkdown';
import { CoachLabWrapUp } from './CoachLabWrapUp';
import { type ICoachLabTurn, spokenLine } from './labLogic';
import { styles } from './styles';
import type { useCoachLabCall } from './useCoachLabCall';

const PROGRESS_STATUS: Partial<Record<string, string>> = {
	coaching: 'The coach is writing its card...',
	prospectReplying: 'The prospect is answering...',
};

/** Whether the rep's ask earned the prospect's next step, so the manager sees why a call opened up or didn't. */
const StepCheckLine = ({ turn }: { turn: ICoachLabTurn }) =>
	turn.stepCheck ? (
		<p className={css(styles.stepCheck, turn.stepCheck.earned && styles.stepCheckEarned)}>
			{turn.stepCheck.earned ? 'You earned the next step. ' : 'Not earned yet. '}
			{turn.stepCheck.reason}
		</p>
	) : null;

const PastTurn = ({ turn }: { turn: ICoachLabTurn }) => (
	<>
		{turn.prospect ? (
			<div className={css(styles.prospectLine)}>
				<span className={css(styles.speaker)}>Prospect</span>
				{turn.prospect}
			</div>
		) : null}
		{turn.card ? (
			<div className={css(styles.pastCard)}>
				<span className={css(styles.cardLabel)}>Coach card {turn.number}</span>
				<CardMarkdown>{turn.card}</CardMarkdown>
				{turn.notes.map((n, i) => (
					<div key={i} className={css(styles.note)}>
						{n}
					</div>
				))}
			</div>
		) : null}
		{turn.rep ? (
			<div className={css(styles.repLine)}>
				<span className={css(styles.speaker)}>You</span>
				{turn.rep}
			</div>
		) : null}
		<StepCheckLine turn={turn} />
	</>
);

export const CoachLabCall = ({
	call,
	coach,
	onCallAgain,
	onNewCall,
	onSave,
}: {
	call: ReturnType<typeof useCoachLabCall>;
	coach: IAIAgentTemplate;
	onCallAgain: () => void;
	onNewCall: () => void;
	onSave: (session: { label: string; markdown: string }) => void;
}) => {
	const { state } = call;
	const [line, setLine] = React.useState('');
	const [planOpen, setPlanOpen] = React.useState(false);

	if (state.phase === 'ended') {
		return <CoachLabWrapUp call={call} coach={coach} onCallAgain={onCallAgain} onNewCall={onNewCall} onSave={onSave} />;
	}

	const current = state.turns[state.turns.length - 1];
	const earlier = state.turns.slice(0, -1);
	const yourTurn = state.phase === 'yourTurn';
	const cardLine = spokenLine(current?.card);

	const say = (text: string) => {
		setLine('');
		call.say(text);
	};

	return (
		<>
			<div className={css(styles.callHeader)}>
				<div>
					<h2 className={css(styles.callTitle)}>Calling {state.leadLabel}</h2>
					<p className={css(styles.help)}>Coach: {coach.name}</p>
				</div>
				<div className={css(styles.actions)}>
					<Button variant='outline' disabled={!state.gamePlan} onClick={() => setPlanOpen(true)}>
						Game Plan
					</Button>
					<Dialog open={planOpen} onOpenChange={setPlanOpen}>
						<DialogPopup>
							<DialogTitle>Game Plan for {state.leadLabel}</DialogTitle>
							<DialogBody styles={[styles.planBody]}>
								<CardMarkdown>{state.gamePlan ?? ''}</CardMarkdown>
							</DialogBody>
							<DialogFooter>
								<DialogClose render={<Button variant='secondary' />}>Close</DialogClose>
							</DialogFooter>
						</DialogPopup>
					</Dialog>
					<Button variant='destructive-secondary' disabled={state.turns.length === 0} onClick={call.hangUp}>
						Hang up
					</Button>
				</div>
			</div>

			{state.phase === 'preparing' ? (
				<div className={css(styles.step)}>
					<LoadingSpinner />
					<p className={css(styles.status)}>{state.progress}</p>
				</div>
			) : null}

			{state.phase === 'failed' ? (
				<Alert tone='danger'>
					<AlertTitle>The call stopped</AlertTitle>
					<AlertDescription>
						{state.error}{' '}
						{call.canRetry
							? 'Try that step again, save what you have so far, or start again.'
							: 'You can save what you have so far, or start again.'}
					</AlertDescription>
					<AlertActions>
						{call.canRetry ? (
							<Button variant='outline' size='sm' onClick={call.retry}>
								Try again
							</Button>
						) : null}
						{state.turns.length > 0 ? (
							<Button variant='outline' size='sm' onClick={call.hangUp}>
								Save what I have
							</Button>
						) : null}
						<Button variant='outline' size='sm' onClick={onNewCall}>
							Start again
						</Button>
					</AlertActions>
				</Alert>
			) : null}

			{state.gamePlan === undefined && state.phase !== 'preparing' && state.turns.length > 0 ? (
				<p className={css(styles.help)}>
					No Game Plan for this lead, so the coach is working from the lead record only.
				</p>
			) : null}

			<div className={css(styles.transcript)}>
				{earlier.map(t => (
					<PastTurn key={t.number} turn={t} />
				))}
				{current?.prospect ? (
					<div className={css(styles.prospectLine)}>
						<span className={css(styles.speaker)}>Prospect</span>
						{current.prospect}
					</div>
				) : null}
				{current?.card ? (
					<div className={css(styles.card)}>
						<span className={css(styles.cardLabel)}>Coach card {current.number}</span>
						<CardMarkdown>{current.card}</CardMarkdown>
						<CardFeedback
							key={current.number}
							notes={current.notes}
							editable={yourTurn}
							prompt='Tell us what you think of this card. The prospect never sees it.'
							onAdd={(kind, text) => call.addNote(kind, text)}
							onRemove={index => call.removeNote(current.number, index)}
						/>
					</div>
				) : null}
				{current?.rep ? (
					<div className={css(styles.repLine)}>
						<span className={css(styles.speaker)}>You</span>
						{current.rep}
					</div>
				) : null}
				{current ? <StepCheckLine turn={current} /> : null}
			</div>

			{PROGRESS_STATUS[state.phase] ? <p className={css(styles.status)}>{PROGRESS_STATUS[state.phase]}</p> : null}

			{yourTurn ? (
				<div className={css(styles.composer)}>
					<TextArea
						styles={[styles.resizableText]}
						placeholder='What you say to the prospect. Use the card, change it, or ignore it. Enter sends; Shift+Enter starts a new line.'
						value={line}
						onChange={e => setLine(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								if (line.trim()) say(line);
							}
						}}
					/>
					<div className={css(styles.actions)}>
						<Button variant='primary' disabled={!line.trim()} onClick={() => say(line)}>
							Say it
						</Button>
						<Button
							variant='secondary'
							disabled={!cardLine}
							title={cardLine ? `Say: "${cardLine}"` : 'This card has no quoted line to use'}
							onClick={() => cardLine && say(cardLine)}
						>
							Say the card's line
						</Button>
					</div>
				</div>
			) : null}
		</>
	);
};
